import { inject, Injectable } from '@angular/core';
import { defer, finalize, map, Observable, of, take } from 'rxjs';

import {
  type ProRataConfirmationDrawerConfig,
  ProRataConfirmationDrawerService,
} from '@ps/core-overlay';
import { hasSupplierLevelQuotaEdit, SurveyQuota } from '@ps/survey-utils';

import { QUOTA_PRO_RATA_CONFIRMATION } from './quota-pro-rata-confirmation.constants';

export interface ProRataConfirmation {
  readonly confirmed: boolean;
  readonly updateSupplier: boolean;
}

export interface QuotaProRataConfirmationOptions {
  /** Open the confirmation stacked over the current drawer. See `DrawerOpenConfig.stacked`. */
  readonly stacked?: boolean;
}

const PROCEED: ProRataConfirmation = { confirmed: true, updateSupplier: true };
const CANCELLED: ProRataConfirmation = {
  confirmed: false,
  updateSupplier: true,
};

/**
 * Confirms a quota current-target change. When the quota has supplier-level
 * edits, the pro-rata drawer asks whether the change should be distributed
 * across suppliers (Continue) or the edited distribution kept (Leave as-is).
 */
@Injectable({ providedIn: 'root' })
export class QuotaProRataConfirmationService {
  private readonly _confirmationDrawer = inject(
    ProRataConfirmationDrawerService,
  );

  public request(
    quota: SurveyQuota,
    options: QuotaProRataConfirmationOptions = {},
  ): Observable<ProRataConfirmation> {
    if (!hasSupplierLevelQuotaEdit(quota)) {
      return of(PROCEED);
    }

    // When opened over another drawer, stack on top and keep a backdrop so an
    // outside click closes this drawer first instead of the one beneath it.
    const drawerConfig: ProRataConfirmationDrawerConfig = options.stacked
      ? { size: 'md', stacked: true }
      : { size: 'md', hasBackdrop: false };

    return defer(() => {
      const drawerRef = this._confirmationDrawer.openRef(
        QUOTA_PRO_RATA_CONFIRMATION,
        drawerConfig,
      );
      return drawerRef.closed$.pipe(
        take(1),
        map((result) => {
          if (result === 'confirm') {
            return PROCEED;
          }
          if (result === 'confirm-advanced') {
            return { confirmed: true, updateSupplier: false };
          }
          return CANCELLED;
        }),
        finalize(() => drawerRef.close()),
      );
    });
  }
}
